import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ExerciseSetBarProps {
  /** Nombre total d'exercices inscrits dans la série (message assistant courant). */
  total: number
  /** Nombre d'exercices ayant déjà une réponse locale. */
  answered: number
  /** Vrai une fois la série validée : la barre bascule en résumé, non interactif. */
  locked: boolean
  /** Bonnes réponses parmi les exercices auto-corrigeables, connu seulement une fois verrouillé. */
  correctCount?: number
  /** Nombre d'exercices auto-corrigeables (mcq, fill) de la série. */
  gradableCount?: number
  onValidate: () => void
}

/**
 * Barre unique de validation en pied de message (DEFAUTS-EXERCICES.md, comportement
 * attendu #3). Remplace les boutons de validation individuels par exercice : elle
 * reste active en permanence — jamais `disabled` — mais demande confirmation avant de
 * valider une série incomplète plutôt que de bloquer l'élève.
 */
export function ExerciseSetBar({
  total,
  answered,
  locked,
  correctCount = 0,
  gradableCount = 0,
  onValidate,
}: Readonly<ExerciseSetBarProps>) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const allAnswered = total > 0 && answered === total

  if (locked) {
    return (
      <div
        role="status"
        className="mt-3 flex items-center gap-2 rounded-lg border border-[var(--color-primary-light)] bg-[var(--color-primary-subtle)] px-3 py-2.5 text-xs font-medium text-[var(--color-neutral-700)]"
      >
        <span aria-hidden="true">🔒</span>
        <span>
          {gradableCount > 0
            ? t('session.exercise_set_locked_summary', { correct: correctCount, total: gradableCount })
            : t('session.exercise_set_locked_no_score')}
        </span>
      </div>
    )
  }

  const handleClick = () => {
    if (allAnswered) {
      onValidate()
      return
    }
    setConfirming(true)
  }

  if (confirming) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-600">{t('session.exercise_set_confirm_incomplete')}</span>
        <button
          type="button"
          onClick={() => {
            setConfirming(false)
            onValidate()
          }}
          className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          {t('common.confirm')}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>
    )
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={handleClick}
        className={`min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          allAnswered
            ? 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]'
            : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
        }`}
      >
        {t('session.exercise_set_validate', { count: total })}
      </button>
    </div>
  )
}
